/**
 * Centralized logger service.
 * Provides structured logging with automatic sanitization and IndexedDB persistence.
 *
 * Features:
 * - Lazy initialization with singleton pattern
 * - Automatic PII sanitization before storage
 * - Correlation ID generation for tracking related operations
 * - Automatic retention management on first access
 * - Specialized API logging with request/response details
 */

import type {
  LogLevel,
  LogCategory,
  LogEntry,
  CreateLogEntryOptions,
  LogMetadata,
  ApiLogMetadata,
  ToastLogMetadata,
} from '@/core/types/models/LogEntry'
import { logStorage } from './logStorage'
import { sanitizeLogData, sanitizeError } from './sanitizers'

/** Flag to track if logger has been initialized */
let isInitialized = false

/**
 * Generates a unique correlation ID using crypto.randomUUID.
 * Falls back to a timestamp-based ID if crypto is not available.
 */
export function generateCorrelationId(): string {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID()
  }

  // Fallback for environments without crypto.randomUUID
  return `${Date.now()}-${Math.random().toString(36).slice(2, 11)}`
}

/**
 * Initializes the logger (runs retention pruning).
 * Called automatically on first log operation.
 */
async function initializeLogger(): Promise<void> {
  if (isInitialized) {
    return
  }

  isInitialized = true

  // Run retention pruning in background (non-blocking)
  queueMicrotask(async () => {
    try {
      await logStorage.runRetention()
    } catch (error) {
      console.error('[Logger] Failed to run retention:', error)
    }
  })
}

/**
 * Logs a message with the specified level and category.
 *
 * @param level - Log severity level
 * @param category - Log category
 * @param message - Human-readable log message
 * @param metadata - Optional additional data (will be sanitized)
 * @param correlationId - Optional correlation ID for tracking related operations
 */
async function log(
  level: LogLevel,
  category: LogCategory,
  message: string,
  metadata?: LogMetadata,
  correlationId?: string
): Promise<LogEntry | null> {
  await initializeLogger()

  try {
    const sanitizedMetadata = metadata ? sanitizeLogData(metadata) : undefined
    const duration = isRecord(metadata) && typeof metadata.duration === 'number' ? metadata.duration : undefined
    const persistedMetadata = stripDuration(sanitizedMetadata)

    const entry: CreateLogEntryOptions = {
      level,
      category,
      message,
      metadata: persistedMetadata,
      correlationId,
    }

    // Add duration if present in metadata (persisted separately)
    if (duration !== undefined) {
      entry.duration = duration
    }

    const logEntry = await logStorage.addLog(entry)

    // Also log to console in development
    if (import.meta.env.DEV) {
      const consoleMethod = level === 'error' ? 'error' : level === 'warn' ? 'warn' : 'info'
      console[consoleMethod](`[${category.toUpperCase()}] ${message}`, metadata ?? '')
    }

    return logEntry
  } catch (error) {
    console.error('[Logger] Failed to persist log:', error)
    return null
  }
}

/**
 * Logs a debug message.
 * Debug logs are only persisted in development mode.
 */
async function debug(
  message: string,
  metadata?: LogMetadata,
  category: LogCategory = 'app'
): Promise<LogEntry | null> {
  // Only persist debug logs in development
  if (!import.meta.env.DEV) {
    return null
  }
  return log('debug', category, message, metadata)
}

/**
 * Logs an informational message.
 */
async function info(
  message: string,
  metadata?: LogMetadata,
  category: LogCategory = 'app'
): Promise<LogEntry | null> {
  return log('info', category, message, metadata)
}

/**
 * Logs a warning message.
 */
async function warn(
  message: string,
  metadata?: LogMetadata,
  category: LogCategory = 'app'
): Promise<LogEntry | null> {
  return log('warn', category, message, metadata)
}

/**
 * Logs an error message.
 *
 * @param message - Error description
 * @param error - Optional error object (will be sanitized)
 * @param metadata - Optional additional context
 * @param category - Log category (default: 'app')
 */
async function error(
  message: string,
  errorObj?: unknown,
  metadata?: LogMetadata,
  category: LogCategory = 'app'
): Promise<LogEntry | null> {
  const sanitizedError = errorObj ? sanitizeError(errorObj) : undefined
  const combinedMetadata: LogMetadata = {
    ...((metadata as Record<string, unknown>) ?? {}),
    ...(sanitizedError ? { error: sanitizedError } : {}),
  }

  return log('error', category, message, combinedMetadata)
}

/**
 * Logs an API request/response.
 * Specialized for HTTP transaction logging with sanitized payloads.
 *
 * @param metadata - API request/response details
 * @param correlationId - Optional correlation ID for request tracking
 */
async function api(
  metadata: ApiLogMetadata,
  correlationId?: string
): Promise<LogEntry | null> {
  const { method, url, status, errorMessage } = metadata

  // Determine log level based on status
  let level: LogLevel = 'info'
  if (status && status >= 400) {
    level = status >= 500 ? 'error' : 'warn'
  }
  if (errorMessage) {
    level = 'error'
  }

  // Build descriptive message
  const statusPart = status ? ` ${status}` : ''
  const message = `${method.toUpperCase()} ${url}${statusPart}`

  return log(level, 'api', message, metadata, correlationId)
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object'
}

function stripDuration(metadata: LogMetadata | undefined): LogMetadata | undefined {
  if (!metadata) {
    return metadata
  }

  if (!isRecord(metadata) || !('duration' in metadata)) {
    return metadata
  }

  const { duration: _duration, ...rest } = metadata as Record<string, unknown>
  return rest as LogMetadata
}

/**
 * Logs a toast notification event.
 *
 * @param metadata - Toast details
 */
async function toast(metadata: ToastLogMetadata): Promise<LogEntry | null> {
  const { severity, summary, detail } = metadata

  // Map toast severity to log level
  const level: LogLevel = severity === 'error' ? 'error' : severity === 'warn' ? 'warn' : 'info'

  const message = detail ? `${summary}: ${detail}` : summary

  return log(level, 'toast', message, metadata)
}

/**
 * Logs a user action.
 *
 * @param action - Description of the user action
 * @param metadata - Optional additional context
 */
async function userAction(
  action: string,
  metadata?: Record<string, unknown>
): Promise<LogEntry | null> {
  return log('info', 'user-action', action, metadata)
}

/**
 * The centralized logger instance.
 * All logging should go through this singleton.
 *
 * @example
 * ```typescript
 * import { logger } from '@/shared/services/logging'
 *
 * // Info logging
 * logger.info('User logged in', { userId: '123' })
 *
 * // Error logging
 * logger.error('Failed to save data', error, { entityId: '456' })
 *
 * // API logging (typically done automatically by interceptor)
 * logger.api({ method: 'GET', url: '/users', status: 200, duration: 150 })
 *
 * // User action logging
 * logger.userAction('Clicked save button', { formId: 'asset-form' })
 * ```
 */
export const logger = {
  debug,
  info,
  warn,
  error,
  api,
  toast,
  userAction,
  generateCorrelationId,
}
